/**
 * community.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Community Forum — Route: /community
 * Placeholder thread content; wire `THREADS` up to a real forum table or
 * external forum (Discourse/Discord) integration later.
 */
import { useState, useEffect, useMemo } from "react";
import {
  MessageCircle, ArrowLeft, ThumbsUp, MessageSquare, Pin, Search, Plus,
} from "lucide-react";
import { BASE_CSS, Sidebar, Topbar, DEFAULT_SETTINGS, buildThemeVars, applyThemeVars, useCurrentUser } from "../shared/AppShell";

const PAGE_CSS = `
  .cm-tags { display:flex; gap:8px; margin-bottom:20px; flex-wrap:wrap; }
  .cm-tag {
    padding:7px 14px; font-size:12px; font-weight:600; border-radius:999px;
    background:var(--surface2); border:1px solid var(--border); color:var(--muted);
    cursor:pointer; transition:all .15s;
  }
  .cm-tag:hover { border-color:var(--border2); color:var(--text); }
  .cm-tag.active { background:var(--accent-dim); border-color:rgba(var(--accent-rgb),.3); color:var(--accent); }

  .cm-thread { display:flex; gap:14px; padding:18px 20px; border-bottom:1px solid var(--border); cursor:pointer; transition:background .15s; }
  .cm-thread:last-child { border-bottom:none; }
  .cm-thread:hover { background:var(--accent-glow); }
  .cm-avatar {
    width:36px; height:36px; border-radius:50%; flex-shrink:0;
    background:linear-gradient(135deg,var(--purple) 0%,var(--blue) 100%);
    display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; color:#000;
  }
  .cm-thread-title { font-size:13.5px; font-weight:600; display:flex; align-items:center; gap:6px; }
  .cm-thread-meta { font-size:11px; color:var(--muted); margin-top:4px; }
  .cm-thread-stats { display:flex; align-items:center; gap:14px; font-size:11px; color:var(--muted); margin-left:auto; flex-shrink:0; }
  .cm-thread-stats span { display:flex; align-items:center; gap:4px; }

  .cm-search-wrap { position:relative; max-width:340px; flex:1; }
  .cm-search-input {
    width:100%; background:var(--surface2); border:1px solid var(--border);
    color:var(--text); border-radius:var(--r-sm); padding:8px 12px 8px 34px;
    font-size:12.5px; outline:none; font-family:var(--sans);
  }
  .cm-search-icon { position:absolute; left:11px; top:50%; transform:translateY(-50%); color:var(--muted); }

  .cm-cta {
    display:flex; align-items:center; justify-content:space-between; gap:16px;
    background:linear-gradient(135deg,rgba(var(--accent-rgb),.08) 0%,transparent 100%);
    border:1px solid rgba(var(--accent-rgb),.2); border-radius:var(--r-lg);
    padding:18px 22px; margin-bottom:24px;
  }
`;

const TAGS = ["All", "General", "Strategy", "Copy Trading", "Bugs & Feedback", "Wins"];

const THREADS = [
  { title: "Best risk management rules for copy trading?", tag: "Copy Trading", author: "M. Adeyemi", pinned: true, replies: 24, likes: 41, time: "2h ago" },
  { title: "How do you size positions on high-volatility pairs", tag: "Strategy", author: "K. Yusuf", replies: 17, likes: 33, time: "5h ago" },
  { title: "Feature request: price alert via WhatsApp", tag: "Bugs & Feedback", author: "T. Chen", replies: 6, likes: 12, time: "1d ago" },
  { title: "Hit a 3-week green streak following the EURUSD signal group 🎉", tag: "Wins", author: "R. Okafor", replies: 29, likes: 88, time: "1d ago" },
  { title: "Anyone else seeing slippage on market orders during NFP?", tag: "General", author: "S. Bello", replies: 11, likes: 9, time: "2d ago" },
  { title: "Weekly strategy share thread — post your setups", tag: "Strategy", author: "Mod Team", pinned: true, replies: 52, likes: 67, time: "3d ago" },
];

export default function CommunityPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTag, setActiveTag] = useState("All");
  const [query, setQuery] = useState("");
  const user = useCurrentUser();

  useEffect(() => { applyThemeVars(buildThemeVars(DEFAULT_SETTINGS)); }, []);

  const filtered = useMemo(() => {
    return THREADS
      .filter((t) => activeTag === "All" || t.tag === activeTag)
      .filter((t) => !query || t.title.toLowerCase().includes(query.toLowerCase()))
      .sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
  }, [activeTag, query]);

  const initials = (name) => name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: BASE_CSS + PAGE_CSS }} />
      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 299 }} />}
      <div className="in-shell">
        <Sidebar open={sidebarOpen} active="/support" />
        <div className="in-right">
          <Topbar onMenu={() => setSidebarOpen((v) => !v)} user={user} title="Community" accent="Forum" />
          <main className="in-main">
            <div className="pg-back" onClick={() => (window.location.href = "/support")}>
              <ArrowLeft size={13} /> Back to Help Center
            </div>

            <div className="pg-hero">
              <div className="pg-hero-grid" />
              <div className="pg-hero-content">
                <div className="pg-hero-label">Community</div>
                <div className="pg-hero-title">Talk trading with <em>other members</em></div>
                <div className="pg-hero-sub">Share strategies, ask questions, and swap notes with traders across the platform.</div>
              </div>
            </div>

            <div className="cm-cta">
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>Have something to share?</div>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>Start a new thread — the community usually replies within a few hours.</div>
              </div>
              <button className="in-btn in-btn-accent in-btn-md"><Plus size={14} /> New thread</button>
            </div>

            <div className="pg-card">
              <div className="pg-card-head" style={{ flexWrap: "wrap" }}>
                <div className="pg-card-title"><MessageCircle size={16} /> Discussions</div>
                <div className="cm-search-wrap">
                  <Search size={14} className="cm-search-icon" />
                  <input className="cm-search-input" placeholder="Search threads…" value={query} onChange={(e) => setQuery(e.target.value)} />
                </div>
              </div>
              <div style={{ padding: "14px 20px 0", display: "flex", gap: 8, flexWrap: "wrap" }} className="cm-tags">
                {TAGS.map((t) => (
                  <div key={t} className={`cm-tag${activeTag === t ? " active" : ""}`} onClick={() => setActiveTag(t)}>{t}</div>
                ))}
              </div>
              <div>
                {filtered.length === 0 ? (
                  <div style={{ padding: "48px 24px", textAlign: "center", color: "var(--muted)", fontSize: 13 }}>No threads match that filter.</div>
                ) : (
                  filtered.map((t, i) => (
                    <div key={i} className="cm-thread">
                      <div className="cm-avatar">{initials(t.author)}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="cm-thread-title">
                          {t.pinned && <Pin size={12} style={{ color: "var(--accent)" }} />}
                          {t.title}
                        </div>
                        <div className="cm-thread-meta">{t.author} · {t.tag} · {t.time}</div>
                      </div>
                      <div className="cm-thread-stats">
                        <span><MessageSquare size={13} /> {t.replies}</span>
                        <span><ThumbsUp size={13} /> {t.likes}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}